/** @file Sets a bone's setup transform from local values, a world pose or an image-pixel position (C6). */
import { z } from 'zod';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { throwError } from '@pose2d/format/throwError';
import type { Id } from '@pose2d/format/types';
import type { CommandContext } from '../commandContext';
import { defineCommand } from '../defineCommand';
import { defineSetter } from '../_shared/defineSetter';
import { assertNotSocketBone } from '../_shared/assertNotSocketBone';
import { setupFromSpace } from './setupFromSpace';

type Space = 'local' | 'world' | 'image';

const value = prim.localTransform.partial().strict();
const setter = defineSetter({ domain: 'bone', kind: 'bone', field: 'setup', schema: value, description: 'bone.setTransform in space "local"' });

/** The image whose pixels space "image" reads (resolved), else null; VALIDATION at `image` when it is missing or stray. */
function pixelFrame(space: Space, image: string | undefined, ctx: CommandContext): Id | null {
  if (space !== 'image') {
    if (image !== undefined) throwError('VALIDATION', undefined, { path: 'image', hint: 'image names the pixel frame of space "image"; drop it or send space: "image"' });
    return null;
  }
  return ctx.resolve('image', image ?? throwError('VALIDATION', undefined, { path: 'image', hint: 'space "image" needs image: the name or id of the image whose pixels x and y are in' }));
}

/** bone.setTransform: space local (default) merges the given local fields; world and image go through setupFromSpace. */
export const command = defineCommand({
  name: 'bone.setTransform',
  description:
    'Set a bone setup transform: value = any of x, y, rotation, scaleX, scaleY, shearX, shearY. space "local" (default): parent-local values in degrees. space "world": skeleton space (y up, degrees CCW); x/y alone move only the bone origin, any other field sets its world rotation/scale/shear (needs inherit "normal"). space "image" with image (name or id): x/y in that image\'s pixels (top-left origin, y down), placed as autorig places the image. Omitted fields keep their value; generated socket bones are refused (edit the socket instead).',
  input: z
    .object({
      target: z.string().min(1),
      value,
      space: z.enum(['local', 'world', 'image']).exactOptional(),
      image: z.string().min(1).exactOptional(),
    })
    .strict(),
  apply(draft, input, ctx) {
    const id = ctx.resolve('bone', input.target);
    assertNotSocketBone(draft, id);
    const space = input.space ?? 'local';
    const image = pixelFrame(space, input.image, ctx);
    if (space === 'local') {
      setter.apply(draft, { target: input.target, value: input.value }, ctx);
      return;
    }
    const bone = draft.bones[id] ?? throwError('NOT_FOUND', { kind: 'bone', ref: input.target });
    bone.setup = setupFromSpace(draft, id, input.value, image);
    draft.meta.updatedAt = ctx.now;
  },
});
